import ClearIcon from '@mui/icons-material/Clear'
import SettingsIcon from '@mui/icons-material/Settings'
import {
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableContainer,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material'
import { range } from 'lodash'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from '../../components/Modal'
import { getDenjiHourSettings } from '../../store/EventById/selectors'
import {
	saveDenjiHour,
	saveDenjiHoursBulk,
} from '../../store/HouseContainer/operations'
import { getActiveSubControl } from '../../store/SubControlById/selectors'
import { Control, DenjiHourSetting, House, Scenario } from '../../types'
import EditDenjiEventForm from './components/EditDenjiEventForm'
import HourlyOverviewBand from './components/HourlyOverviewBand'
import HourlyRuleRow from './components/HourlyRuleRow'

type Props = {
	house: House
	scenario: Scenario
	control: Control
}

const isActive = (s: DenjiHourSetting): boolean =>
	s.enabled && (s.soilTmpAbove !== null || s.soilHmdBelow !== null)

// 末尾の .0 を除去（20.0 → 20）
const fmt = (n: number): string => `${+n}`
const condLabel = (s: DenjiHourSetting): string => {
	const parts: string[] = []
	if (s.soilTmpAbove !== null) parts.push(`土壌温度≧${fmt(s.soilTmpAbove)}℃`)
	if (s.soilHmdBelow !== null) parts.push(`土壌湿度＜${fmt(s.soilHmdBelow)}%`)
	return parts.length ? parts.join(' / ') : '条件なし'
}

const toNullableNumber = (v: string): number | null => {
	if (v === '') return null
	const n = Number(v)
	return isNaN(n) ? null : n
}

function DenjiManageTab({ house, scenario, control }: Props) {
	const activeSubc = useSelector((s) => getActiveSubControl(s, control))
	const byHour = useSelector((s) =>
		getDenjiHourSettings(s, scenario.eventIds, activeSubc),
	)

	const [mode, setMode] = React.useState<'each' | 'bulk'>('each')
	const activeByHour = range(24).map((h) => isActive(byHour[h]))

	return (
		<Box sx={{ mt: 1 }}>
			<HourlyOverviewBand activeByHour={activeByHour} />

			<Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
				<ToggleButtonGroup
					size="small"
					exclusive
					value={mode}
					onChange={(_e, v) => v && setMode(v)}
				>
					<ToggleButton value="each">個別編集</ToggleButton>
					<ToggleButton value="bulk">まとめて編集</ToggleButton>
				</ToggleButtonGroup>
			</Box>

			{mode === 'bulk' ? (
				<DenjiInlineEditor
					houseId={house.id}
					scenarioId={scenario.id}
					subControlId={activeSubc.id}
					byHour={byHour}
				/>
			) : (
				<TableContainer component={Paper}>
					<Table size="small">
						<TableBody>
							{range(24).map((h) => (
								<DenjiHourRow
									key={h}
									houseId={house.id}
									setting={byHour[h]}
									scenarioId={scenario.id}
									subControlId={activeSubc.id}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	)
}

// モーダル無しで 24時間を直接インライン編集し、変更分をまとめて保存する。
type InlineProps = {
	houseId: number
	scenarioId: number
	subControlId: number
	byHour: Record<number, DenjiHourSetting>
}
const DenjiInlineEditor = (props: InlineProps) => {
	const dispatch = useDispatch()
	const { byHour, houseId, scenarioId, subControlId } = props
	const [rows, setRows] = React.useState<DenjiHourSetting[]>(() =>
		range(24).map((h) => ({ ...byHour[h] })),
	)

	const update = (hour: number, patch: Partial<DenjiHourSetting>) =>
		setRows((rs) => rs.map((r) => (r.hour === hour ? { ...r, ...patch } : r)))

	const isChanged = (r: DenjiHourSetting): boolean => {
		const o = byHour[r.hour]
		return (
			r.soilTmpAbove !== o.soilTmpAbove || r.soilHmdBelow !== o.soilHmdBelow
		)
	}
	const changed = rows.filter(isChanged)

	// 0時の値を全時間帯にコピー
	const applyFirstToAll = () => {
		const tpl = rows[0]
		setRows((rs) =>
			rs.map((r) => ({
				...r,
				soilTmpAbove: tpl.soilTmpAbove,
				soilHmdBelow: tpl.soilHmdBelow,
			})),
		)
	}

	const save = () => {
		if (!changed.length) return
		dispatch(
			saveDenjiHoursBulk(
				changed.map((r) => ({
					houseId,
					scenarioId,
					subControlId,
					hour: r.hour,
					eventId: r.eventId,
					enabled: r.soilTmpAbove !== null || r.soilHmdBelow !== null,
					soilTmpAbove: r.soilTmpAbove,
					soilHmdBelow: r.soilHmdBelow,
					startTime: r.startTime,
					endTime: r.endTime,
					oldTriggerIds: r.triggerIds,
				})),
			),
		)
	}

	return (
		<Paper sx={{ p: 1.5 }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 1,
					gap: 1,
					flexWrap: 'wrap',
				}}
			>
				<Typography variant="caption" color="text.secondary">
					各時間帯を直接編集（空欄でその条件なし・両方空欄でOFF）
				</Typography>
				<Button size="small" onClick={applyFirstToAll}>
					0時を全時間に適用
				</Button>
			</Box>

			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: '3.2rem 1fr 1fr 2rem',
					gap: '4px 8px',
					alignItems: 'center',
					maxHeight: 380,
					overflowY: 'auto',
					pr: 0.5,
				}}
			>
				<Typography variant="caption" color="text.secondary">
					時間
				</Typography>
				<Typography variant="caption" color="text.secondary">
					土壌温度≧(℃)
				</Typography>
				<Typography variant="caption" color="text.secondary">
					土壌湿度＜(%)
				</Typography>
				<span />

				{rows.map((r) => {
					const active = r.soilTmpAbove !== null || r.soilHmdBelow !== null
					return (
						<React.Fragment key={r.hour}>
							<Typography
								variant="body2"
								sx={{ color: active ? 'text.primary' : 'text.disabled' }}
							>
								{r.hour}時
							</Typography>
							<TextField
								type="number"
								size="small"
								value={r.soilTmpAbove ?? ''}
								placeholder="—"
								onChange={(e) =>
									update(r.hour, {
										soilTmpAbove: toNullableNumber(e.target.value),
									})
								}
								inputProps={{ step: 0.1 }}
								fullWidth
							/>
							<TextField
								type="number"
								size="small"
								value={r.soilHmdBelow ?? ''}
								placeholder="—"
								onChange={(e) =>
									update(r.hour, {
										soilHmdBelow: toNullableNumber(e.target.value),
									})
								}
								inputProps={{ step: 1, min: 0, max: 100 }}
								fullWidth
							/>
							<Tooltip title="この時間をクリア">
								<span>
									<IconButton
										size="small"
										disabled={!active}
										onClick={() =>
											update(r.hour, {
												soilTmpAbove: null,
												soilHmdBelow: null,
											})
										}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</React.Fragment>
					)
				})}
			</Box>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					gap: 1.5,
					mt: 1.5,
				}}
			>
				<Typography variant="caption" color="text.secondary">
					{changed.length > 0 ? `${changed.length}件 変更あり` : '変更なし'}
				</Typography>
				<Button
					variant="contained"
					disableElevation
					disabled={changed.length === 0}
					onClick={save}
				>
					保存
				</Button>
			</Box>
		</Paper>
	)
}

type RowProps = {
	houseId: number
	setting: DenjiHourSetting
	scenarioId: number
	subControlId: number
}
const DenjiHourRow = (props: RowProps) => {
	const [open, setOpen] = React.useState(false)
	const dispatch = useDispatch()
	const { houseId, setting, scenarioId, subControlId } = props

	return (
		<HourlyRuleRow
			hourLabel={`${setting.hour}時`}
			enabled={setting.enabled}
			active={isActive(setting)}
			summary={setting.enabled ? condLabel(setting) : 'OFF'}
		>
			<Button variant="outlined" size="small" onClick={() => setOpen(true)}>
				<SettingsIcon fontSize="small" />
			</Button>
			<Modal open={open} closeModal={() => setOpen(false)}>
				<EditDenjiEventForm
					initialValues={{
						enabled: setting.enabled,
						soilTmpAbove: setting.soilTmpAbove,
						soilHmdBelow: setting.soilHmdBelow,
					}}
					disabled={false}
					handleSubmit={(field) => {
						dispatch(
							saveDenjiHour({
								houseId,
								scenarioId,
								subControlId,
								hour: setting.hour,
								eventId: setting.eventId,
								enabled: field.enabled,
								soilTmpAbove: field.soilTmpAbove,
								soilHmdBelow: field.soilHmdBelow,
								startTime: setting.startTime,
								endTime: setting.endTime,
								oldTriggerIds: setting.triggerIds,
							}),
						)
						setOpen(false)
					}}
				/>
			</Modal>
		</HourlyRuleRow>
	)
}

export default DenjiManageTab
